import { Tooltip } from "@/components/atoms";

/**
 * The live-tracking dot (RM-Client 7039-22249) — 12px, beside the flight number in the flight card's
 * header.
 *
 * It answers the question the whole ticket exists for before the reader has to ask it: the pickup on this
 * booking is not a number someone typed and forgot, it is attached to a flight that is being watched. The
 * footer below explains what happened; this says something is still happening.
 *
 * In the card HEADER, not in `FlightNumberField`. The field is where a flight is CAPTURED — it is on the
 * Address step, it is empty half the time, and a live indicator there would claim tracking for a number
 * that has not resolved. The header sits on a card that only exists once the lookup answered.
 *
 * ## The animation
 *
 * A dot-scale ping (`animate-pulse-dot`), not the existing `pulse-ring`. That one is a 4px→12px boxShadow
 * halo sized for a pending-action CARD; on a 6px dot it blooms to 30px across and swallows the flight
 * number. Tailwind keyframes cannot be parameterised, so a second keyframe is the only way to have the
 * same gesture at this size — see `tailwind.config.cjs`.
 *
 * Held still under `prefers-reduced-motion`, following the repo's one precedent
 * (`PendingAssistantRequests.tsx:39`). The dot still reads as live: the colour and the tooltip carry the
 * meaning, and the movement was only ever the attention-getter.
 */
export const FlightTrackingDot = ({ flightNumber }: { flightNumber: string }) => (
  <Tooltip content={`Tracking ${flightNumber} live. If the arrival time changes, we'll adjust your pickup — nothing for you to do.`}>
    {/* The accessible name states the fact rather than describing the graphic; a screen reader gets the
        same sentence the tooltip shows, which hover-only content otherwise never reaches. */}
    <span
      role="img"
      aria-label={`Tracking ${flightNumber} live`}
      className="relative inline-flex size-3 shrink-0 items-center justify-center"
    >
      <span aria-hidden className="absolute inset-0 rounded-full bg-danger-light" />
      <span aria-hidden className="relative size-1.5 animate-pulse-dot rounded-full bg-danger motion-reduce:animate-none" />
    </span>
  </Tooltip>
);
